/* 장소 사진·후기 (/place/:placeId)
 *
 * 모임 상세의 장소 시트에서 [전체보기]로 들어옵니다.
 * 시트는 일부만 보여주고, 다 보고 쓰는 일은 여기서 합니다.
 */
import { useCallback, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppScreen, Section, SectionHeader, StarRating, Button } from 'components/ds';
import { StorePhotoSlots } from 'components/place/StorePhotoSlots';
import type { StoreSlot } from 'components/place/StorePhotoSlots';
import { VisitPhotoSection } from 'components/place/VisitPhotos';
import { VisitPhotoDetail } from 'components/place/VisitPhotoDetail';
import { VisitPhotoWrite } from 'components/place/VisitPhotoWrite';
import type { VisitPhoto } from 'components/place/VisitPhotos';
import { BlockConfirmModal } from 'components/common/BlockConfirmModal';
import { ReportSheet } from 'components/common/ReportSheet';
import type { ReportReasonCode } from 'components/common/ReportSheet';
import { PlaceReviewRepository } from 'repository/placeReviewRepository';
import { BlockRepository } from 'repository/blockRepository';
import { usePlaceReviews } from 'hooks/usePlaceReviews';
import { usePlacePhotoUpload } from 'hooks/usePlacePhotoUpload';
import { useAlert } from 'contexts/AlertContext';
import styles from './PlacePhotosPage.module.css';

export const PlacePhotosPage = () => {
    const { t } = useTranslation();
    const { placeId = '' } = useParams();
    const [search] = useSearchParams();
    const placeName = search.get('name') || '';
    const meetingId = search.get('meetingId') || undefined;
    /* 사진을 올릴 수 있는 상태인지는 모임 상세가 판단해서 넘겨 줍니다.
       (진행 중 + 200m 이내) 여기서 다시 계산하면 판단이 두 곳으로 갈라집니다. */
    const canAdd = search.get('canAdd') === '1';

    const { showAlert, showConfirm } = useAlert();
    const { storePhotos, posts, rating, reviewCount, hasMore, loadMore, reload, toggleHelpful } =
        usePlaceReviews(placeId);
    const { busy, uploadStorePhoto, submitVisitPost } = usePlacePhotoUpload({ placeId, placeName, meetingId });

    const [writing, setWriting] = useState(false);
    const [detail, setDetail] = useState<number | null>(null);
    const [tag, setTag] = useState('');
    /** 고치는 중인 기록. null 이면 새로 쓰는 중 */
    const [editing, setEditing] = useState<VisitPhoto | null>(null);
    /** 차단 확인을 띄운 대상 */
    const [blocking, setBlocking] = useState<VisitPhoto | null>(null);
    /** 신고 시트를 띄운 대상 */
    const [reporting, setReporting] = useState<VisitPhoto | null>(null);

    // 태그는 올라온 후기에서 모읍니다 — 아무도 안 쓴 태그를 보여줄 이유가 없습니다
    const tags = useMemo(() => {
        const seen = new Map<string, number>();
        posts.forEach(p => p.tags?.forEach(x => seen.set(x, (seen.get(x) || 0) + 1)));
        return Array.from(seen.entries()).sort((a, b) => b[1] - a[1]).slice(0, 12).map(([x]) => x);
    }, [posts]);

    const filtered = useMemo(
        () => (tag ? posts.filter(p => p.tags?.includes(tag)) : posts),
        [posts, tag],
    );

    /* 내 기록 지우기. postId 는 review_id 입니다(usePlaceReviews 에서 옮겨 담습니다).
       서버가 WHERE user_no 로 한 번 더 확인하므로 남의 글은 지워지지 않습니다. */
    const removePost = useCallback(async (post: VisitPhoto) => {
        const yes = await showConfirm({ message: t('visitPhoto.deleteConfirm') });
        if (!yes) return;
        const res: any = await PlaceReviewRepository.deleteReview(post.postId);
        /* onRequest 는 에러를 던지지 않고 응답을 그대로 돌려줍니다 —
           상태 코드를 봐야 실패를 압니다. */
        const ok = typeof res?.status === 'number' && res.status >= 200 && res.status < 300;
        await showAlert({ message: t(ok ? 'visitPhoto.deleted' : 'visitPhoto.deleteFail') });
        if (ok) { setDetail(null); reload(); }
    }, [showConfirm, showAlert, t, reload]);

    /* 신고 보내기. 성공 여부는 응답을 직접 봅니다 — onRequest 는 던지지 않습니다. */
    const sendReport = useCallback(async (
        reviewId: number, code: ReportReasonCode, text?: string,
    ): Promise<boolean> => {
        const res: any = await PlaceReviewRepository.reportReview(reviewId, code, text);
        return typeof res?.status === 'number' && res.status >= 200 && res.status < 300;
    }, []);

    const reportPost = useCallback(async (code: ReportReasonCode, text: string) => {
        const target = reporting;
        if (!target) return;
        const ok = await sendReport(target.postId, code, text);
        setReporting(null);
        await showAlert({ message: t(ok ? 'posts.reportDone' : 'posts.reportFail') });
        /* 신고가 쌓여 내려갔을 수도 있으니 목록을 다시 받습니다 */
        if (ok) reload();
    }, [reporting, sendReport, showAlert, t, reload]);

    const blockAuthor = useCallback(async (reason?: string, alsoReport?: boolean) => {
        const target = blocking;
        if (!target) return;
        try {
            await BlockRepository.onPostBlock(Number(target.userNo), reason);
            /* 인앱 알림 배너 등 다른 화면에도 알립니다 — MemberActionMenu 와 같은 신호 */
            window.dispatchEvent(new CustomEvent('dokocrew:blockChange', {
                detail: { userNo: Number(target.userNo) },
            }));
            /* "신고도 함께" 를 골랐으면 이 기록도 신고합니다. 차단은 나만 안 보는
               것이고 신고는 모두에게서 내리는 것이라, 둘은 따로 나갑니다.
               신고가 실패해도 차단은 이미 됐으므로 되돌리지 않습니다. */
            if (alsoReport) {
                try { await sendReport(target.postId, 'ETC', reason); }
                catch (re) { console.warn('신고 적재 실패', re); }
            }
            await showAlert({ message: t('visitPhoto.blocked', { name: target.nickname }) });
            setDetail(null);
            reload();
        } catch (e: any) {
            await showAlert({ message: e?.response?.data?.message || t('visitPhoto.blockFail') });
        }
    }, [blocking, sendReport, showAlert, t, reload]);

    const handleStorePick = useCallback(async (slot: StoreSlot, files: File[]) => {
        const ok = await uploadStorePhoto(slot, files);
        await showAlert({ message: t(ok ? 'visitPhoto.uploaded' : 'visitPhoto.uploadFail') });
        if (ok) reload();
    }, [uploadStorePhoto, showAlert, t, reload]);

    return (
        <AppScreen title={placeName || t('visitPhoto.title')} bar="sub">
            {(rating || reviewCount > 0) && (
                <Section spacing="tight">
                    <SectionHeader title={placeName} />
                    {rating ? <StarRating value={rating} count={reviewCount} source="CREW" /> : null}
                </Section>
            )}

            <Section divided>
                <SectionHeader title={t('storePhoto.sectionTitle')} />
                <StorePhotoSlots photos={storePhotos} canAdd={canAdd && !busy} onPick={handleStorePick} />
            </Section>

            <Section divided last>
                <VisitPhotoSection
                    posts={filtered}
                    total={filtered.length}
                    /* 가보기 전에는 남길 게 없습니다. 가게 사진 자리는 이미
                       canAdd 를 보는데 여기만 항상 켜 있어, 안 가본 곳에도
                       「나도 올리기」가 떴습니다. */
                    canAdd={canAdd}
                    onAdd={() => setWriting(true)}
                    onOpen={(_p, i) => setDetail(i)}
                    tags={tags}
                    activeTag={tag}
                    onChangeTag={setTag}
                />
                {/* 태그로 거른 상태에서는 더 불러와도 어긋나므로 전체일 때만 */}
                {!tag && hasMore && (
                    <div className={styles.more}>
                        <Button block variant="secondary" onClick={loadMore}>{t('common.more')}</Button>
                    </div>
                )}
            </Section>

            <VisitPhotoDetail
                open={detail !== null}
                posts={filtered}
                index={detail ?? 0}
                onIndexChange={setDetail}
                onClose={() => setDetail(null)}
                onHelpful={toggleHelpful}
                onEdit={(post) => { setDetail(null); setEditing(post); }}
                onDelete={removePost}
                onBlock={setBlocking}
                onReport={setReporting}
            />
            <VisitPhotoWrite
                open={writing}
                onClose={() => setWriting(false)}
                placeName={placeName}
                submitting={busy}
                onSubmit={async (draft) => {
                    const { ok, photosFailed } = await submitVisitPost(draft);
                    setWriting(false);
                    await showAlert({
                        message: t(!ok ? 'visitPhoto.uploadFail'
                            : photosFailed > 0 ? 'visitPhoto.postedPhotoFail'
                            : 'visitPhoto.uploaded'),
                    });
                    if (ok) reload();
                }}
            />

            {/* 고치기 — 쓰기 시트를 지금 값으로 채워 엽니다.
                사진은 수정 API 가 받지 않아 시트가 그 칸을 감춥니다. */}
            <VisitPhotoWrite
                open={editing !== null}
                editing
                onClose={() => setEditing(null)}
                placeName={placeName}
                initial={editing ? {
                    rating: editing.rating,
                    content: editing.content,
                    tags: editing.tags,
                } : undefined}
                onSubmit={async (draft) => {
                    if (!editing) return;
                    const res: any = await PlaceReviewRepository.updateReview(editing.postId, {
                        placeId,
                        rating: draft.rating,
                        content: draft.content,
                        tags: draft.tags.join(','),
                    });
                    const ok = typeof res?.status === 'number' && res.status >= 200 && res.status < 300;
                    setEditing(null);
                    await showAlert({ message: t(ok ? 'visitPhoto.updated' : 'visitPhoto.updateFail') });
                    if (ok) reload();
                }}
            />

            <BlockConfirmModal
                isOpen={blocking !== null}
                targetName={blocking?.nickname || ''}
                onClose={() => setBlocking(null)}
                onConfirm={({ reason, report }) => blockAuthor(reason, report)}
            />

            <ReportSheet
                open={reporting !== null}
                onClose={() => setReporting(null)}
                onSubmit={reportPost}
            />
        </AppScreen>
    );
};
